import React from 'react';
import { get, cloneDeep, merge, find, isEmpty } from 'lodash';

// React view component to render
import { MemberSecuritySettingsInfoService } from 'services/memberSecuritySettingsInfo';
import { memberSecuritySettingsInfo as MemberSecuritySettingsInfoView } from 'ui/views/widgets/memberSecuritySettingsInfo';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';

import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { getLabel } from 'utils/getLabel';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as successOverlay from 'utils/successOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberSecuritySettingsInfo } from 'ui/containers/MemberSecuritySettingsInfo';
 *
 * // render
 * <MemberSecuritySettingsInfo propName='propValue'/>
 */
export class MemberSecuritySettingsInfo extends React.Component {
    
  /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );
        
        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            showCustomErrorView: false
        };

        this.editEmailAction = this.editEmailAction.bind( this );
        this.resetPassword = this.resetPassword.bind( this );
        this.sendOTP = this.sendOTP.bind( this );

        // Member Info Settings Service
        this.memberSecuritySettingsInfoService = new MemberSecuritySettingsInfoService();

        // Member Info Settings data fetch
        this.fetchComponentData();
    }

    async fetchComponentData() {
       
        log.debug( 'MemberSecuritySettingsInfo.fetchComponentData()' );

        // cancel ongoing request
        this.memberSecuritySettingsInfoService.cancel();

        try {
            const { status, data }  = await this.memberSecuritySettingsInfoService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was failure
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                if( ! isEmpty( get( data, 'description', {} ) ) ) {
                    this.setState( {
                        isFetching: false,
                        showCustomErrorView: true,
                        componentData: data

                        // networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                    } );
                } else {

                    this.setState( {
                        isFetching: false,
                        networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                    } );
                }

               
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }
    

    async editEmailAction( { action, mode, field } ) {
        log.debug( 'MemberCommunicationSettings.addEditActionHandler()' );
        
        // cancel ongoing request
        this.memberSecuritySettingsInfoService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberSecuritySettingsInfoService.fetchAddEditForm( {
            url: action.url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.EDIT_SECURITY_SETTING,
                    showCloseButton: false,
                    ignoreOutsideClick: true,
                },
                config: {},
                data: {
                    ...data,
                    mode,
                    itemData: field,
                    onCancelClick: () => {
                        overlay.close();
                    },
                    onSumbitClick: async ( { email } ) => {
                         overlay.close();

                        // find action with `name: save/edit` depending upon the action
                        const formAction = find( get( data, 'actions', [] ), { name: 'save' } );
                        
                         // cancel ongoing request
                         this.memberSecuritySettingsInfoService.cancel();

                         pageLevelPreloader.show();

                         const reqParams = cloneDeep( this.props.requestPayload );

                        reqParams.context.operation = CRUD_OPERATION.UPDATE;

                        // get status and service data
                        const { _status, _data } = await this.memberSecuritySettingsInfoService.updateEmail( {
                            url: formAction.url,
                            data: merge(
                                {
                                    data: {
                                        memberId: reqParams.memberId,
                                        email: email
                                    }
                                }, reqParams ),
                        } );

                        // hide loader
                        pageLevelPreloader.hide();

                        // check if response status was success
                         if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            const securityInfo = cloneDeep( this.state.componentData.securityInfo );
                            securityInfo.values[ field.name ] = _data.email;

                            const closeAction = find( get( data, 'actions', [] ), { name: 'close' } );
                            
                            overlay.open( {
                                settings: {
                                    modalType: OVERLAY_VIEWS.PROMPT_VIEW,
                                    showCloseButton: false,
                                    ignoreOutsideClick: true,
                                },
                                config: {},
                            data: {
                                    message: getLabel( closeAction.title ),
                                    onOkClick: () => {
                                        overlay.close();
                                    },
                                }
                             } );

                            /*----------*/
                            this.setState( {
                                componentData: {
                                    ...this.state.componentData,
                                    securityInfo, // update new items
                                },
                            } );

                        } else {
                            errorOverlay.open(); // show error
                        }
                    },
                }
            } );
        }
    }

    async resetPassword( { action, mode, field } ) {
        log.debug( 'MemberresetPasswordSettings.resetPassword()' );

            // cancel ongoing request
            this.memberSecuritySettingsInfoService.cancel();

            // show page level preloader
            pageLevelPreloader.show();

            // get status and service data
            const { status, data } = await this.memberSecuritySettingsInfoService.fetchAddEditForm( {
                url: action.url
            } );

            // show page level preloader
            pageLevelPreloader.hide();
            
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.EDIT_PASSWORD_SECURITY_SETTING,
                        showCloseButton: false,
                        ignoreOutsideClick: true,
                    },
                    config: {},
                    data: {
                        message: getLabel( data.fields[ 0 ][ 'title' ] ),
                        field: get( data, 'fields[1]' ),
                        ...data,
                        mode,
                        itemData: field,
                        onCancelClick: () => {
                            overlay.close();
                        },
                        onSumbitClick: async () => {

                            overlay.close();

                            const formAction = find( get( data, 'actions', [] ), { name: 'sendlink' } );
                        
                            pageLevelPreloader.show();
                            
                            // cancel ongoing request
                            this.memberSecuritySettingsInfoService.cancel();
   
                            const reqParams = cloneDeep( this.props.requestPayload );
   
                            reqParams.context.operation = CRUD_OPERATION.UPDATE;

                            // get status and service data
                            const { _status } = await this.memberSecuritySettingsInfoService.resetPassword( {
                                url: formAction.url,
                                data: merge(
                                    {
                                        data: {
                                            memberId: reqParams.memberId
                                        }
                                    }, reqParams ),
                            } );

                            pageLevelPreloader.hide();

                            // check if response status was success
                            if ( SERVICE_RESPONSE_STATUS.SUCCESS !== _status ) {
                                errorOverlay.open(); // show error
                            }
                        }
                    }
                } );
            }
        }

    async sendOTP( { action, mode, field } ) {
        log.debug( 'MemberresetPasswordSettings.sendOTP()' );

            // cancel ongoing request
            this.memberSecuritySettingsInfoService.cancel();

            // show page level preloader
            pageLevelPreloader.show();

            // get status and service data
            const { status, data } = await this.memberSecuritySettingsInfoService.fetchAddEditForm( {
                url: action.url
            } );

            // show page level preloader
            pageLevelPreloader.hide();
            
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.SECURITY_SETTING_SEND_OTP,
                        showCloseButton: false,
                        ignoreOutsideClick: true,
                    },
                    config: {},
                    data: {
                        message: getLabel( data.fields[ 0 ][ 'title' ] ),
                        field: get( data, 'fields[1]' ),
                        ...data,
                        mode,
                        itemData: field,
                        onCancelClick: () => {
                            overlay.close();
                        },
                        onSumbitClick: async ( ) => {

                            overlay.close();

                            const formAction = find( get( data, 'actions', [] ), { name: 'sendlink' } );

                            // cancel ongoing request
                            this.memberSecuritySettingsInfoService.cancel();

                            // show page level preloader
                            pageLevelPreloader.show();

                            const reqParams = cloneDeep( this.props.requestPayload );

                            reqParams.context.operation = CRUD_OPERATION.UPDATE;

                           // get status and service data
                           const { _status } = await this.memberSecuritySettingsInfoService.resetPassword( {
                               url: formAction.url,
                               data: merge(
                                   {
                                       data: {
                                           memberId: reqParams.memberId
                                       }
                                   }, reqParams ),
                           } );

                           // show page level preloader
                            pageLevelPreloader.hide();

                           if( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {
                            successOverlay.open( {
                                message: getLabel( formAction.onSuccess ),
                            } );
                            } else {
                                errorOverlay.open();
                            }
                        }
                    }
                } );
            }
        }

    /******************************************** */
    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberSecuritySettingsInfoView
                isFetching= { this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                requestPayload={ this.props.requestPayload }
                url={ this.props.widget.url }
                { ...get( this.state, 'componentData', {} ) }
                editEmailAction = { this.editEmailAction }
                resetPassword = { this.resetPassword }
                sendOTP = { this.sendOTP }
                showCustomErrorView={ this.state.showCustomErrorView }
            />
        );
    }
}

// set display name
MemberSecuritySettingsInfo.displayName = 'MemberSecuritySettingsInfo';

// set default props
MemberSecuritySettingsInfo.defaultProps = {};

// prop types
MemberSecuritySettingsInfo.propTypes = {};
